import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { parse } from '@vanillaes/csv';
import ConsumerRoutePath from 'app/consumer-app/ConsumerRoutePath';
import { UrlQueryKey } from 'app/core/api/api-core/api-constants';
import { useAssignedProductsLite } from 'app/core/api/product-assignment/api-product-assignment';
import Button from 'app/shared/components/button/Button';
import Icon from 'app/shared/components/icon/Icon';
import IconLib, {
  SmartphoneSvg,
} from 'app/shared/components/icon/icon-lib';
// import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import LoadingOverlay from 'app/shared/page-parts/LoadingOverlay';
import { CUSTOM_BLUE } from 'app/styles/theme';
import { LSKey } from 'app/tools/hooks/local-storage/local-storage.types';
import useLocalState from 'app/tools/hooks/local-storage/use-local-storage';
import { noneToEmpty } from 'app/tools/utils';
import Axios from 'axios-observable';
import { useObservableState } from 'observable-hooks';
import React, { useEffect, useMemo } from 'react';
import {
  isMobile,
  mobileModel,
  mobileVendor,
} from 'react-device-detect';
// import { useHistory } from 'react-router-dom';
import { from, map } from 'rxjs';
import styled from 'styled-components/macro';
import Tile, { TileContainer } from '../../../shared/frames/Tile';
import ProductCard from './components/ProductCard';

const CloseButton = styled(Button)`
  position: absolute;
  top: 15px;
  right: 15px;
  color: white;
`;

type DeviceListHeaders = [
  'Retail Branding',
  'Marketing Name',
  'Device',
  'Model'
];

type Props = {
  setIsWelcomeDrawerOpen: (isVisible: boolean) => unknown;
};
export default function Products({
  setIsWelcomeDrawerOpen,
}: Props): JSX.Element {
  // const history = useHistory();
  // const { showInstruction } = useShowInfo();

  // Save in localstorage whether 'add this device' has been clicked before
  const [autoAddedDevice, setAutoAddedDevice] = useLocalState(
    LSKey.AUTO_ADDED_DEVICE,
    false
  );

  const { resource: assignedProducts, loadingMissing } =
    useAssignedProductsLite();
  useEffect(() => {
    if (assignedProducts && assignedProducts.length > 0) {
      setIsWelcomeDrawerOpen(false);
    } else {
      setIsWelcomeDrawerOpen(true);
    }
  }, [assignedProducts, setIsWelcomeDrawerOpen]);
  const wide = (i: number) => [2, 7].includes(i);

  // useEffect(() => {
  //   if (assignedProducts?.length === 0) {
  //     showInstruction({
  //       instructionName: 'welcomeAddProduct',
  //       noShowAfterDismiss: true,
  //       title: "You're in!",
  //       message:
  //         "Now for the fun part - let's get your first product added.",
  //       actions: [
  //         {
  //           text: 'Scan barcode',
  //           callback: () =>
  //             history.push(ConsumerRoutePath.PRODUCT_SCAN),
  //         },
  //         {
  //           text: 'Search product',
  //           callback: () =>
  //             history.push(ConsumerRoutePath.PRODUCT_SCAN),
  //         },
  //       ],
  //     });
  //   }
  // }, [assignedProducts?.length, history, showInstruction]);

  const deviceInfo = useObservableState(
    // Triggers the getInfo promise once, converts it to an observable,
    // stores the returned value to `deviceInfo`.
    from(useMemo(() => Device.getInfo(), []))
  );

  const [{ lookUpDevice }] = useObservableState(
    () =>
      Axios.get<string>(
        // TODO: fetch this dynamically from our backend
        `${process.env.PUBLIC_URL}/android_devices.csv`
      ).pipe(
        map(({ data }) => parse<DeviceListHeaders>(data)),
        map(csv => ({
          lookUpDevice: (model?: string) => {
            if (!model) return;
            const deviceRow = csv?.find(r => r[3]?.includes(model));
            if (!deviceRow) return;
            return {
              name: deviceRow[1],
              brand: deviceRow[0],
            };
          },
        }))
      ),
    { lookUpDevice: () => undefined }
  );

  const deviceSearchUrl = useMemo(() => {
    // Return empty if on computer, or lookup table load failed.
    if (!lookUpDevice || !isMobile) return;
    // NOTE: prioritising react-device-detect vendor value since it
    // gives 'Apple' on iOS (the desired result), whereas capacitor
    // shows 'Apple Computer, Inc.'.
    // NOTE: disabling found brand when not on native device as it can
    // give browser manufacturer instead of phone manufacturer
    const deviceMake = Capacitor.isNativePlatform()
      ? noneToEmpty(mobileVendor) || deviceInfo?.manufacturer
      : undefined;
    // Prioritise capacitor model value to give accurate iPhone names
    const deviceModel = deviceInfo?.model || noneToEmpty(mobileModel);

    if (!deviceModel) return; // Nothing of use if this is blank
    const deviceLookup = lookUpDevice(deviceModel);
    const terms = [
      deviceLookup?.brand || deviceMake || [],
      deviceLookup?.name || deviceModel || [],
    ].flat();

    const encodedTerms = encodeURIComponent(terms.join(' '));
    const urlRoot = ConsumerRoutePath.PRODUCT_SEARCH_CATALOG;
    // NOTE: only provide results on mobile devices and tablets, since
    // you get weird results that are not accurate, on desktop.
    return {
      pathname: urlRoot,
      query: { [UrlQueryKey.SEARCH]: encodedTerms },
    };
  }, [deviceInfo?.manufacturer, deviceInfo?.model, lookUpDevice]);

  return (
    <>
      {assignedProducts && assignedProducts?.length > 0 && (
        <h3 style={{ color: '#31AD31', fontWeight: 'normal' }}>
          Your Products
        </h3>
      )}
      <LoadingOverlay loadingComplete={!loadingMissing} />
      <TileContainer>
        {assignedProducts?.map((assignedProduct, i) => (
          <Tile key={assignedProduct.id} wide={wide(i)}>
            <ProductCard
              variant={wide(i) ? 'wide' : undefined}
              item={assignedProduct}
            />
          </Tile>
        ))}
        {(assignedProducts?.length ?? 3) <= 2 && (
          <>
            {/* <Tile>
              <ProductCard
                iconButtonIcon={IconLib.SCAN}
                iconButtonText="Scan Barcode"
                to={ConsumerRoutePath.PRODUCT_SCAN}
              />
            </Tile>
            <Tile>
              <ProductCard
                iconButtonIcon={IconLib.SEARCH}
                iconButtonText="Search Product"
                to={ConsumerRoutePath.PRODUCT_SEARCH_CATALOG}
              />
            </Tile> */}
          </>
        )}
        {deviceSearchUrl && !autoAddedDevice && (
          <Tile>
            <ProductCard
              iconButtonIcon={<SmartphoneSvg fill="white" />}
              iconButtonText="Add This Device"
              iconButtonColor={CUSTOM_BLUE}
              to={deviceSearchUrl}
              onClick={() => setAutoAddedDevice(true)}
            />
            <CloseButton
              iconButton={{ size: 'small' }}
              onClick={() => setAutoAddedDevice(true)}
            >
              <Icon icon={IconLib.CROSS_BOXED} />
            </CloseButton>
          </Tile>
        )}
      </TileContainer>
    </>
  );
}
